/**
 * Cost round 3 on the console: unusual spend (runs, tasks and person-days far past their peers'
 * median), cost per outcome (task, plan, pull request, roadmap item) and the one figure the
 * project's Work tab shows beside each task and plan. Every amount through useMoney; a 403 (not a
 * manager of what is asked) shows nothing rather than an error.
 */

import { ANOMALY_MULTIPLE, ANOMALY_TRAILING_DAYS, anomalyWords, type CostOutcomeGrouping, type CostOutcomeLine, type CostOutcomes } from '@aioe/shared';
import { ChartColumn, CircleExclamation, ListCheck } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { useMoney } from '../money.tsx';
import { SubjectName } from './SubjectPicker.tsx';
import { Button, ChoiceSelect, Empty, ErrorNote, ListBlock, ListGroup, ListRow } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';

/** A refusal for want of a role is "nothing to show here", not an error. */
const forbidden = (e: unknown) => e instanceof ApiError && e.status === 403;
const orNothing = <T,>(p: Promise<T>): Promise<T | null> => p.catch((e: unknown) => (forbidden(e) ? null : Promise.reject(e)));

const STATE_WORDS: Record<string, string> = {
  todo: 'To do',
  'to-do': 'To do',
  backlog: 'Backlog',
  ready: 'Ready',
  doing: 'In progress',
  'in-progress': 'In progress',
  in_progress: 'In progress',
  active: 'Active',
  review: 'In review',
  'in-review': 'In review',
  blocked: 'Blocked',
  done: 'Done',
  complete: 'Done',
  completed: 'Done',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  draft: 'Draft',
  proposed: 'Proposed',
  approved: 'Approved',
  rejected: 'Rejected',
  open: 'Open',
  merged: 'Merged',
  closed: 'Closed',
  planned: 'Planned',
  shipped: 'Shipped',
};

/** A task's, plan's, pull request's or roadmap item's state in words. */
export function stateWords(state: string | undefined): string | undefined {
  if (!state) return undefined;
  const known = STATE_WORDS[state.toLowerCase()];
  if (known) return known;
  const s = state.replace(/[-_]+/g, ' ').trim();
  return s ? s[0]!.toUpperCase() + s.slice(1) : undefined;
}

/** Unusual spend: what cost far more than its peers, each said in words, one row each. */
export function UnusualSpend({ since }: { since: string }) {
  const tone = VIEW_TONES.costs!;
  const { format } = useMoney();
  const res = useLoad(() => orNothing(routes.costAnomalies(since)), [since], 300_000);
  if (res.data === null) return null;
  const rows = res.data?.anomalies ?? [];
  return (
    <ListGroup
      title="Unusual spend"
      count={rows.length || undefined}
      description={`Runs, tasks and a person's day that cost more than ${ANOMALY_MULTIPLE} times the median of the same team and model over the last ${ANOMALY_TRAILING_DAYS} days. The project's owners are told.`}
      empty={res.data && rows.length === 0 ? <Empty tone={tone} icon={<CircleExclamation />} title="Nothing unusual" body="Every run, task and day in this period cost about what its peers did. Anything far past them shows here, in words." compact /> : undefined}
    >
      {!res.data ? <ListBlock className="text-sm text-muted">{res.error ? <ErrorNote error={res.error} /> : 'Looking for unusual spend…'}</ListBlock> : null}
      {rows.map((a) => (
        <ListRow
          key={a.key}
          title={`${anomalyWords(a, (n) => format(n))}.`}
          description={
            <>
              {a.projectId ? (
                <Link to={`/projects/${encodeURIComponent(a.projectId)}?tab=work`} className="text-accent hover:underline">
                  {a.projectName ?? a.projectId}
                </Link>
              ) : (
                'No project'
              )}
              {' · '}
              {a.teamName ?? (a.teamId || 'no team')} · <span className="mono">{a.model}</span>
              {a.principal && a.kind !== 'person-day' ? (
                <>
                  {' · '}
                  <SubjectName id={a.principal} fallback={a.principal} />
                </>
              ) : null}
              {` · the median was ${format(a.medianUsd)} across ${a.peers} ${a.peers === 1 ? 'peer' : 'peers'} · flagged ${timeAgo(a.flaggedAt)}`}
            </>
          }
        />
      ))}
    </ListGroup>
  );
}

const OUTCOMES: Record<CostOutcomeGrouping, string> = { task: 'By task', plan: 'By plan', pullRequest: 'By pull request', roadmap: 'By roadmap item' };
const UNATTRIBUTED: Record<CostOutcomeGrouping, string> = {
  task: 'carried no board task',
  plan: 'served no plan',
  pullRequest: 'led to no pull request',
  roadmap: 'served no roadmap item a run named',
};
const SHOWN = 12;

/** One money figure for a line: metered spend, or the seats' value when that is all it had. */
function lineFigure(l: Pick<CostOutcomeLine, 'usd' | 'subscriptionUsd'>, format: (n: number) => string): { text: string; seats: boolean } {
  if (l.usd === 0 && l.subscriptionUsd > 0) return { text: format(l.subscriptionUsd), seats: true };
  return { text: format(l.usd), seats: false };
}

/** Cost per outcome across the organisation (or the areas a manager runs), for the Costs page's period: one row per piece of work, its figure at the end. */
export function Outcomes({ since, until, areaId }: { since: string; until: string; areaId?: string }) {
  const tone = VIEW_TONES.costs!;
  const { format } = useMoney();
  const [by, setBy] = useState<CostOutcomeGrouping>('task');
  const [all, setAll] = useState(false);
  const res = useLoad(() => orNothing(routes.costOutcomes({ since, until, by, areaId })), [since, until, by, areaId]);
  if (res.data === null) return null;
  const lines = res.data?.lines ?? [];
  const shown = all ? lines : lines.slice(0, SHOWN);
  const rest = res.data?.unattributed;
  return (
    <ListGroup
      title="Outcomes"
      count={lines.length || undefined}
      description="What each piece of work cost, from the calls that named its task. Metered spend; where a line says “value on paid seats”, its calls were all on paid seats."
      actions={<ChoiceSelect label="Outcome" className="w-48 max-w-full" value={by} options={OUTCOMES} onChange={(v) => (setBy(v), setAll(false))} />}
      empty={res.data && lines.length === 0 ? <Empty tone={tone} icon={<ListCheck />} title="No work named in this period" body="A workbench names the board task each model call served; once it does, what each task, plan, pull request and roadmap item cost shows here." compact /> : undefined}
    >
      {!res.data ? <ListBlock className="text-sm text-muted">{res.error ? <ErrorNote error={res.error} /> : 'Reading what the work cost…'}</ListBlock> : null}
      {shown.map((l) => {
        const fig = lineFigure(l, format);
        const words = [stateWords(l.state), fig.seats ? 'value on paid seats' : ''].filter(Boolean) as string[];
        return (
          <ListRow
            key={`${l.projectId}/${l.key}`}
            title={l.name}
            description={
              l.projectId || words.length ? (
                <>
                  {l.projectId ? (
                    <Link to={`/projects/${encodeURIComponent(l.projectId)}?tab=work`} className="hover:underline">
                      {l.projectName ?? l.projectId}
                    </Link>
                  ) : null}
                  {words.map((t, i) => (l.projectId || i > 0 ? ` · ${t}` : t))}
                </>
              ) : undefined
            }
            value={<span className={`tabular-nums ${fig.seats ? '' : 'font-medium text-foreground'}`}>{fig.text}</span>}
          />
        );
      })}
      {lines.length > SHOWN ? (
        <ListBlock>
          <Button size="sm" variant="ghost" onPress={() => setAll(!all)}>
            {all ? 'Show the largest only' : `Show all ${lines.length}`}
          </Button>
        </ListBlock>
      ) : null}
      {rest && rest.calls > 0 ? (
        <ListBlock className="text-xs text-muted">
          <span className="tabular-nums">{format(rest.usd)}</span> of spend in this period, over {rest.calls.toLocaleString()} {rest.calls === 1 ? 'call' : 'calls'}, {UNATTRIBUTED[by]}.
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/** One piece of work in the project's list (its Work part): its name, its state, its figure at the end. */
function OutcomeRow({ l, format }: { l: CostOutcomeLine; format: (n: number) => string }) {
  const fig = lineFigure(l, format);
  const words = [stateWords(l.state), fig.seats ? 'value on paid seats' : ''].filter(Boolean) as string[];
  return <ListRow title={l.name} description={words.length ? words.join(' · ') : undefined} value={<span className={`tabular-nums ${fig.seats ? '' : 'font-medium text-foreground'}`}>{fig.text}</span>} />;
}

const WORK_DAYS = 90;
const workSince = () => new Date(Date.now() - WORK_DAYS * 86_400_000).toISOString();

/** A project's task costs keyed `<root>/<task id>` (and `<root>/<team>/<task id>`), for the queue's rows. Undefined while loading or when not a manager. */
export function useTaskCosts(projectId: string): Map<string, CostOutcomeLine> | undefined {
  const res = useLoad(() => orNothing(routes.costOutcomes({ since: workSince(), by: 'task', projectId })).catch(() => null), [projectId], 300_000);
  if (!res.data) return undefined;
  const map = new Map<string, CostOutcomeLine>();
  for (const l of res.data.lines) {
    if (!l.id) continue;
    map.set(`${l.rootId ?? ''}/${l.teamId ?? ''}/${l.id}`, l);
    if (!map.has(`${l.rootId ?? ''}//${l.id}`)) map.set(`${l.rootId ?? ''}//${l.id}`, l);
  }
  return map;
}

/** The figure beside a task in a list: one number, or nothing. */
export function TaskCost({ costs, rootId, board, taskId }: { costs: Map<string, CostOutcomeLine> | undefined; rootId: string; board: string; taskId: string }) {
  const { format } = useMoney();
  const l = costs?.get(`${rootId}/${board}/${taskId}`) ?? costs?.get(`${rootId}//${taskId}`);
  if (!l) return null;
  const fig = lineFigure(l, format);
  return (
    <span className="text-sm tabular-nums text-muted" title={fig.seats ? 'Value of its calls on paid seats, last 90 days' : 'Metered spend on this task, last 90 days'}>
      {fig.text}
    </span>
  );
}

/** The project's Work tab: what each task and plan cost, one figure a row. Shows nothing to someone who may not read costs. */
export function WorkCosts({ projectId }: { projectId: string }) {
  const tone = VIEW_TONES.projects!;
  const { format } = useMoney();
  const [by, setBy] = useState<'task' | 'plan'>('task');
  const [all, setAll] = useState(false);
  const since = useState(workSince)[0];
  const res = useLoad<CostOutcomes | null>(() => orNothing(routes.costOutcomes({ since, by, projectId })), [projectId, by, since]);
  if (res.data === null) return null;
  const lines = res.data?.lines ?? [];
  const shown = all ? lines : lines.slice(0, SHOWN);
  const total = lines.reduce((n, l) => n + l.usd, 0);
  return (
    <ListGroup
      title="What the work cost"
      count={lines.length || undefined}
      description={lines.length ? `${format(total)} over the last ${WORK_DAYS} days, metered spend.` : `Metered spend over the last ${WORK_DAYS} days, by the task or plan each call served.`}
      actions={
        <Segment aria-label="Tasks or plans" size="sm" selectedKey={by} onSelectionChange={(k) => (setBy(k === 'plan' ? 'plan' : 'task'), setAll(false))} className="w-fit">
          <Segment.Item id="task">Tasks</Segment.Item>
          <Segment.Item id="plan">Plans</Segment.Item>
        </Segment>
      }
      empty={res.data && lines.length === 0 ? <Empty tone={tone} icon={<ChartColumn />} compact title={by === 'task' ? 'No task with a cost yet' : 'No plan with a cost yet'} body={by === 'task' ? `No call named one of this project’s tasks in the last ${WORK_DAYS} days.` : `No plan of this project had work that cost anything in the last ${WORK_DAYS} days.`} /> : undefined}
    >
      {!res.data ? <ListBlock className="text-sm text-muted">{res.error ? <ErrorNote error={res.error} /> : 'Reading what the work cost…'}</ListBlock> : null}
      {shown.map((l) => (
        <OutcomeRow key={l.key} l={l} format={format} />
      ))}
      {lines.length > SHOWN ? (
        <ListBlock>
          <Button size="sm" variant="ghost" onPress={() => setAll(!all)}>
            {all ? 'Show the largest only' : `Show all ${lines.length}`}
          </Button>
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
